import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useParams } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import {
  GET_CLINIC_BY_PUBLIC_ID,
  GetClinicByPublicIdData,
  GetClinicByPublicIdVariables,
  PublicClinic,
} from "@app/service/patientEducation";

interface PatientEducationContextValue {
  publicClinicId: string | null;
  clinic: PublicClinic | null;
  loading: boolean;
  error: boolean;
}

const PatientEducationContext = createContext<
  PatientEducationContextValue | undefined
>(undefined);

export const PatientEducationProvider: FC = () => {
  const { publicClinicId } = useParams<{ publicClinicId: string }>();
  const [clinic, setClinic] = useState<PublicClinic | null>(null);
  const [error, setError] = useState<boolean>(false);

  const [getClinic, { loading }] = useLazyQuery<
    GetClinicByPublicIdData,
    GetClinicByPublicIdVariables
  >(GET_CLINIC_BY_PUBLIC_ID, {
    onCompleted: (data) => {
      if (data.clinicByPublicId) {
        setClinic(data.clinicByPublicId);
        setError(false);
      } else {
        setError(true);
      }
    },
    onError: () => {
      setError(true);
    },
  });

  useEffect(() => {
    // Only fetch if we don't already have the clinic data and we have a publicClinicId
    if (publicClinicId && !clinic && !error) {
      getClinic({ variables: { publicId: publicClinicId } });
    }
  }, [publicClinicId, clinic, error, getClinic]);

  const value = useMemo<PatientEducationContextValue>(
    () => ({
      publicClinicId: publicClinicId ?? null,
      clinic,
      loading,
      error,
    }),
    [publicClinicId, clinic, loading, error]
  );

  return (
    <PatientEducationContext.Provider value={value}>
      <Outlet />
    </PatientEducationContext.Provider>
  );
};

export const usePatientEducation = (): PatientEducationContextValue => {
  const context = useContext(PatientEducationContext);
  if (!context) {
    throw new Error(
      "usePatientEducation must be used within PatientEducationProvider"
    );
  }
  return context;
};
